import getConfig from 'next/config'
import axios from 'axios'

const { hashFields } = require('./normalizePhone')

const { publicRuntimeConfig } = getConfig()

const fullUrl = (url, slug) => {
  return slug
    ? `${publicRuntimeConfig.apiUrl}/${url}/${slug}`
    : `${publicRuntimeConfig.apiUrl}/${url}`
}

const generateKey = (base, slug = '', params = {}) => {
  let key = slug ? `/${base}/${slug}` : `/${base}`
  const paramsKeys = Object.keys(params)
  paramsKeys.length > 0 &&
    paramsKeys.forEach((item, ind) => {
      if (params[item] !== undefined) {
        if (ind === 0) {
          key = `${key}?${item}=${params[item]}`
        } else {
          key = `${key}&${item}=${params[item]}`
        }
      }
    })

  return key
}

const isClient = () => typeof window !== 'undefined'

// Меню и расписание нужны на каждой клиентской навигации.
// Остальные ответы уже лежат в pageProps и не должны дублироваться в HTML.
const CLIENT_CACHE_KEYS = new Set(['/menu', '/working-mode'])

export function createApi() {
  let cacheData = {}

  function fillCache(data) {
    cacheData = { ...data }
  }

  function extractCache() {
    const payload = {}

    Object.keys(cacheData).forEach((key) => {
      if (CLIENT_CACHE_KEYS.has(key)) {
        payload[key] = cacheData[key]
      }
    })

    return payload
  }

  const loadMore =
    (url, perPage) =>
    async (page, query = {}, { shouldCache = true } = {}) => {
      const { slug = '', cancelToken = axios.CancelToken.source().token, ...params } = query
      page = Number(page) || 1

      const key = generateKey(url, slug, params)
      const lastCachedPage = (cacheData[key] && cacheData[key].lastPage) || 0

      if (isClient() && cacheData[key] && cacheData[key].lastPage >= page) {
        // console.warn('return from cache: ', key)
        return {
          ...cacheData[key],
          items: cacheData[key].items.slice(0, page * perPage),
        }
      }

      return axios
        .get(fullUrl(url, slug), {
          cancelToken,
          params: { ...params, page: lastCachedPage + 1, count: (page - lastCachedPage) * perPage },
        })
        .then((response) => {
          const responseData = response.data
          const { meta, links, data, ...other } = responseData
          const cachedItems = (cacheData[key] && cacheData[key].items) || []

          const result = {
            ...other,
            meta,
            items: [...cachedItems, ...(data || [])],
            total: responseData.meta.total,
            perPage,
            lastPage: page,
          }

          if (shouldCache) {
            cacheData[key] = result
          }

          return result
        })
        .catch(onError('get'))
    }

  const get =
    (url) =>
    (query = {}, { shouldCache = true } = {}) => {
      const { slug = '', cancelToken = axios.CancelToken.source().token, ...params } = query

      const key = generateKey(url, slug, params)
      // console.log(cacheData);
      if (isClient() && cacheData[key]) {
        // console.warn('return from cache: ', key)
        return Promise.resolve(cacheData[key])
      }

      return axios
        .get(fullUrl(url, slug), { cancelToken, params: params })
        .then((response) => {
          if (shouldCache) {
            cacheData[key] = response.data
          }
          // console.log('get',response.data)
          return response.data
        })
        .catch(onError('get'))
    }

  const post = (url) => (data) => {
    return axios
      .post(fullUrl(url), data)
      .then((response) => response.data)
      .catch(onError('post'))
  }

  const hash = (payload) => {
    if (!payload || !payload.data) return post('hash')(payload)
    return post('hash')({ ...payload, data: hashFields(payload.data) })
  }

  const onError = (method) => (err) => {
    if (axios.isCancel(err)) {
      console.log(err)
      throw err
    }

    const response = err.response
    if (!response) {
      const error = new Error('Network Error')
      error.method = method
      throw error
    }

    const error = new Error(
      response.status === 404 ? `Not Found: ${response.config.url}` : `Internal Error: ${response.config.url}`
    )
    error.statusCode = response.status === 404 ? 404 : 500
    error.httpStatus = response.status
    error.url = response.config.url
    error.params = response.config.params
    error.method = method
    error.data = response.data
    throw error
  }

  return {
    appointment: get('appointments-booking'),
    appointmentCreate: post('appointments-booking/create'),
    touch: post('touch'),
    specialistsByDirection: get('specialists-direction'),
    // attributionCreate: post('attribution/create'),
    articles: loadMore('articles', 9),
    article: get('articles'),
    consultationCreate: post('consultation/create'),
    contacts: get('contacts'),
    directions: get('directions'),
    hash,
    menu: get('menu'),
    mainPage: get('main-page'),
    promotions: get('promo'),
    reviews: loadMore('reviews', 6),
    reviewsCreate: post('reviews/create'),
    search: loadMore('search', 15),
    service: get('directions/service'),
    schedule: get('working-mode'),
    specialists: loadMore('specialists', 100),
    specialist: get('specialists'),
    type: get('type'),
    wiki: loadMore('encyclopedia', 14),
    wikiArticle: get('encyclopedia'),
    fillCache,
    extractCache,
  }
}

export default createApi()
